import { FC } from 'react';
import { useDateService } from 'framework/utils';
import { ProcessedLeaveYear } from '../../shared/types';
import { useShowContext } from 'framework/show';

type BalanceFieldName = keyof Pick<ProcessedLeaveYear, 'paidLeavesRemainedBalance' | 'sickDaysRemainedBalance'>;

type ExpiredBalanceFieldName = keyof Pick<ProcessedLeaveYear, 'paidLeavesExpiredBalance' | 'sickDaysExpiredBalance'>;

type DayOnDemandFieldName = keyof Pick<ProcessedLeaveYear, 'dayOnDemandsRemainedBalance'>;

interface AvailableFieldProps {
  balanceField: BalanceFieldName;
  dayOnDemandField?: DayOnDemandFieldName;
  expiredBalanceField?: ExpiredBalanceFieldName;
}

const getAvailableBalanceLabel = (balance: string | number, expiredBalance?: string | number) => {
  const numericBalance = Number(balance);
  const numericExpiredBalance = Number(expiredBalance);
  const availableBalance = numericBalance - numericExpiredBalance;

  if (!numericExpiredBalance) {
    return `${availableBalance}d`;
  }

  return `${availableBalance}d (${numericExpiredBalance}d expired)`;
};

const AvailableField: FC<AvailableFieldProps> = ({ balanceField, dayOnDemandField, expiredBalanceField }) => {
  const { data } = useShowContext<ProcessedLeaveYear>();
  const period = data?.getData();
  const { isCurrentTimeInPeriod } = useDateService();
  const isCurrentYearPeriod = isCurrentTimeInPeriod(period?.startDate, period?.endDate);

  if (!period) return null;
  if (period.hideBalanceColumns) return null;

  const balanceValue = period[balanceField];
  const expiredBalanceValue = expiredBalanceField ? period[expiredBalanceField] : 0;

  if (
    dayOnDemandField &&
    period.hasDayOnDemandBalance &&
    isCurrentYearPeriod &&
    period[dayOnDemandField] &&
    balanceValue
  ) {
    return (
      <span>
        {getAvailableBalanceLabel(balanceValue, expiredBalanceValue)}/{period[dayOnDemandField]}d on demand
      </span>
    );
  }

  if (!balanceValue) return null;

  return <span>{getAvailableBalanceLabel(balanceValue, expiredBalanceValue)}</span>;
};

export default AvailableField;
